import SyosUiContainer from './SyosUiContainer';
import SyosScreenContainer from './SyosScreenContainer';
import SyosEvents from './utils/SyosEvents';
import SyosPageComponent from './components/SyosPageComponent';
import SvgIcon from '../utils/SvgIcon';
import IconSprites from '../utils/IconSprites';
import type SyosController from './SyosController';
import { Screen } from './api/types';
import type SyosMediaQuery from './utils/SyosMediaQuery';

const HTML = `
  <button class="btn btn-default tn-syos-btn-view-screens" aria-describedby="${SyosScreenContainer.sectionListId}">
    <span class="tn-syos-btn-view-screens__contents">   
      ${SvgIcon.getHtml(IconSprites.ChevronLeft, 'tn-syos-btn-view-screens__icon')}
      <span class="tn-syos-btn-view-screens__label"></span>
    </span>
  </button>
`;

class SyosBtnViewScreens extends SyosUiContainer {
  $btnViewScreens: JQuery<HTMLElement>;

  $btnViewScreensLabel: JQuery<HTMLElement>;

  screens: Screen[] = [];

  syosMediaQuery?: SyosMediaQuery;

  constructor(syosController: SyosController, syosMediaQuery: SyosMediaQuery) {
    super(syosController, '.tn-syos-btn-view-screens__container');

    this.syosMediaQuery = syosMediaQuery;

    this.$el.html(HTML);

    this.$btnViewScreens = this.find('.tn-syos-btn-view-screens');
    this.$btnViewScreensLabel = this.find('.tn-syos-btn-view-screens__label');

    this.$btnViewScreens.hide();

    this.$btnViewScreens.on('click', this.onBtnClick);

    syosMediaQuery.on(SyosEvents.MOBILE_ENTER, this.onMobileEnter);
    syosMediaQuery.on(SyosEvents.MOBILE_LEAVE, this.onMobileLeave);

    this.syosController.once(SyosEvents.SCREENS_DATA_LOADED, this.onScreenDataLoaded);
    this.syosController.once(SyosEvents.SEAT_LIST_DATA_LOADED, this.onSeatListDataLoaded);
  }

  onBtnClick = () => {
    this.syosController.unsetCurrentScreen();
  };

  onScreenDataLoaded = ({ screens }: { screens: Screen[] }) => {
    this.screens = screens;

    this.syosController.on(SyosEvents.SCREEN_UPDATED, this.onScreenUpdated);
    this.syosController.on(SyosEvents.SEAT_LIST_DATA_LOADED, this.onSeatListDataLoaded);

    if (this.syosMediaQuery?.testIsMobile()) {
      this.onMobileEnter();
    } else {
      this.onMobileLeave();
    }

    this.updateBtnDisplay();
  };

  getUiState() {
    const isInSeatView = !!this.syosController.getCurrentScreen();
    const isMobile = this.syosMediaQuery?.testIsMobile();
    const isSingleScreen = this.screens.length === 1;
    const isUsingCustomBirdsEye = this.syosController.getIsUsingCustomBirdsEye();
    const isConfiguredToSkipOverview = isSingleScreen && this.screens[0].skipScreenSelection === true;

    return {
      isSingleScreen,
      isInSeatView,
      isMobile,
      isConfiguredToSkipOverview,
      isUsingCustomBirdsEye,

      // Convenience negations
      isInOverview: !isInSeatView,
      isMultiScreen: !isSingleScreen,
      isDesktop: !isMobile,
      isConfiguredToPresentOverview: !isConfiguredToSkipOverview,
      isNotUsingCustomBirdsEye: !isUsingCustomBirdsEye,
    };
  }

  onMobileEnter = () => {
    const { isInSeatView } = this.getUiState();

    if (isInSeatView) {
      this.syosController.unsetCurrentScreen();
    } else {
      this.updateBtnDisplay();
    }
  };

  onMobileLeave = () => {
    const { isInOverview, isConfiguredToSkipOverview } = this.getUiState();

    if (isInOverview && isConfiguredToSkipOverview) {
      window.setTimeout(() => {
        this.syosController.setCurrentScreen(this.screens[0]);
      });
    } else {
      this.updateBtnDisplay();
    }
  };

  onScreenUpdated = ({ screen }: { screen: Screen }) => {
    if (!screen) {
      this.$btnViewScreens.hide();
    }
  };

  onSeatListDataLoaded = () => {
    this.updateBtnDisplay();

    this.$btnViewScreens[0].focus();
  };

  updateBtnDisplay = () => {
    const label = this.getButtonLabel();

    if (label === null) {
      this.$btnViewScreens.hide();
    } else {
      this.$btnViewScreensLabel.html(label);
      this.$btnViewScreens.show();
    }
  };

  /**
   * Returns the appropriate label based on current state.
   * Returns `null` if button should not be visible.
   */
  getButtonLabel(): null | string {
    const {
      isMobile,
      isInOverview,
      isDesktop,
      isMultiScreen,
      isSingleScreen,
      isConfiguredToPresentOverview,
      isNotUsingCustomBirdsEye,
    } = this.getUiState();

    if (isInOverview) {
      // Button should always be hidden @ the overview
      return null;
    }

    if (isSingleScreen && isConfiguredToPresentOverview && isNotUsingCustomBirdsEye) {
      return SyosPageComponent.backToSeatMapButtonLabel;
    }

    if (isMultiScreen) {
      return SyosPageComponent.viewFullMapLink;
    }

    if (isMobile) {
      return SyosPageComponent.viewEventDetailsButtonLabel;
    }

    if (isDesktop && isConfiguredToPresentOverview) {
      return SyosPageComponent.backToSeatMapButtonLabel;
    }

    // In all other cases, button should be hidden
    return null;
  }

  destroy() {
    this.syosMediaQuery?.removeListener(SyosEvents.MOBILE_ENTER, this.onMobileEnter);
    this.syosMediaQuery?.removeListener(SyosEvents.MOBILE_LEAVE, this.onMobileLeave);
    this.syosMediaQuery = undefined;

    this.$btnViewScreens.off('click', this.onBtnClick);

    super.destroy();
  }
}

export default SyosBtnViewScreens;
